import { dataAttributeProps } from '@pbk-web/utils';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip from '../tooltip/Tooltip';
import ProfileInitials from './ProfileInitials';
import UploadedImage from './UploadedImage';
import type { AvatarRole } from './useAvatarRoleNames';
import useAvatarRoleNames from './useAvatarRoleNames';

export type { AvatarRole };

export enum ImageSize {
  XS,
  S,
  M,
  L,
  XXS,
}

type AvatarSize = {
  initials: string;
  image: string;
};

export const ProfileSizeMap: Record<ImageSize, AvatarSize> = {
  [ImageSize.XXS]: { initials: 'min-w-5 w-5 h-5 text-[10px]', image: 'min-w-[20px] w-[20px] h-[20px] text-[10px]' },
  [ImageSize.XS]: { initials: 'min-w-7 w-7 h-7 text-xs', image: 'min-w-[26px] w-[26px] h-[26px] text-xs' },
  [ImageSize.S]: { initials: 'min-w-9 w-9 h-9 text-pbk-12', image: 'min-w-[34px] w-[34px] h-[34px] text-pbk-12' },
  [ImageSize.M]: { initials: 'min-w-11 w-11 h-11 text-pbk-14', image: 'min-w-[42px] w-[42px] h-[42px] text-pbk-14' },
  [ImageSize.L]: { initials: 'min-w-17 w-17 h-17 text-2xl', image: 'min-w-[66px] w-[66px] h-[66px] text-2xl' },
};

type AvatarUser = {
  id?: string;
  firstName?: string;
  lastName?: string;
  userImageId?: string;
};

type AvatarProps = {
  user?: AvatarUser;
  roles?: AvatarRole | AvatarRole[];
  size?: ImageSize;
  withMargin?: boolean;
  className?: string;
  isCurrentUser?: boolean;
  icon?: ReactNode;
  /** Overrides the default name+roles tooltip with custom content (e.g. a presence "Active now" card). */
  tooltip?: ReactNode;
  [key: string]: unknown;
};

const isLocal = location.host.indexOf('localhost') !== -1;

const ProfileAvatar: FC<AvatarProps> = (props) => {
  const { user, size, withMargin, className = '', roles, isCurrentUser = false, icon, tooltip } = props;
  const sizeCss = ProfileSizeMap[size ?? ImageSize.M];
  const marginCss = withMargin ? '-ml-2' : '';
  const { t } = useTranslation(['common']);

  const roleNames = useAvatarRoleNames(roles || [], t);

  const defaultTooltip = useMemo(() => {
    if (!user?.firstName || !user?.lastName) return undefined;
    return (
      <>
        <div>
          {user.firstName} {user.lastName}
        </div>
        <div className="text-greyscale-15">{roleNames}</div>
      </>
    );
  }, [user?.firstName, user?.lastName, roleNames]);

  const tooltipText = tooltip ?? defaultTooltip;

  return (
    <div className="relative flex w-fit flex-col justify-center">
      <Tooltip text={tooltipText}>
        {(tooltip) => (
          <div {...tooltip}>
            {icon ? (
              <div
                className={`${sizeCss.initials} ${marginCss} rounded-full bg-white p-[2px] shadow-xl transition-all duration-300 ease-in-out hover:z-20`}
                {...dataAttributeProps(props)}
              >
                <div className="bg-greyscale-5 inline-flex h-full w-full items-center justify-center rounded-full">{icon}</div>
              </div>
            ) : !user?.userImageId && !isLocal ? (
              <ProfileInitials
                firstName={user?.firstName ?? ''}
                lastName={user?.lastName ?? ''}
                id={user?.id ?? ''}
                isCurrentUser={isCurrentUser}
                className={className}
                marginCss={marginCss}
                sizeCss={sizeCss.initials}
                {...dataAttributeProps(props)}
              />
            ) : (
              <UploadedImage
                uniqueId={user?.id ?? ''}
                fileId={user?.userImageId}
                className={`relative z-0 rounded-full ${size === ImageSize.XS ? 'p-px' : 'p-[2px]'} ${sizeCss.image} ${className} hover:bg-accent-4-55 rounded-full bg-white shadow-xl transition-all duration-300 ease-in-out hover:z-20 [&:is([data-user-count]):not([data-user-count="0"]):not([data-user-count="1"])]:hover:-mt-[0.3rem] ${marginCss}`}
                {...dataAttributeProps(props)}
                placeholder={
                  <ProfileInitials
                    firstName={user?.firstName ?? ''}
                    lastName={user?.lastName ?? ''}
                    id={user?.id ?? ''}
                    isCurrentUser={isCurrentUser}
                    className={className}
                    marginCss={marginCss}
                    sizeCss={sizeCss.initials}
                    {...dataAttributeProps(props)}
                  />
                }
              />
            )}
          </div>
        )}
      </Tooltip>
    </div>
  );
};

export default ProfileAvatar;
